import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Banknote, Check, Home as HomeIcon, Building2, Truck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, mediaUrl } from "../services/api";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { formatPrice } from "../utils/formatPrice";
import CouponInput from "../components/checkout/CouponInput";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const inputCls = "w-full bg-white border border-[#EAE8E4] rounded-xl px-4 py-3 text-sm text-[#111111] outline-none focus:border-[#111111] transition-colors font-body";

export default function Checkout() {
  const navigate = useNavigate();
  const { items, subtotal, discount, coupon, clearCart, updateWilayaFee } = useCart();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [wilayas, setWilayas] = useState([]);
  const [deliveryType, setDeliveryType] = useState("home");
  const [deliveryFee, setDeliveryFee] = useState(null);

  const [form, setForm] = useState({
    first_name: "", last_name: "", phone: "", email: "",
    wilaya: "", commune: "", address: "", delivery_notes: "",
  });

  // Pre-fill from logged-in user
  useEffect(() => {
    if (user) {
      const parts = (user.full_name || "").split(" ");
      setForm((f) => ({
        ...f,
        first_name: parts[0] || "",
        last_name: parts.slice(1).join(" ") || "",
        phone: user.phone || "",
        email: user.email || "",
        wilaya: user.wilaya || "",
        address: user.address || "",
      }));
    }
  }, [user]);

  // Load wilaya prices once
  useEffect(() => {
    api.get("/wilayas").then((r) => setWilayas(r.data)).catch(() => {});
  }, []);

  // Redirect to cart if empty
  useEffect(() => {
    if (items.length === 0) navigate("/cart");
  }, [items.length, navigate]);

  // Recompute fee whenever wilaya or delivery type changes
  useEffect(() => {
    if (!form.wilaya) {
      setDeliveryFee(null);
      updateWilayaFee(null);
      return;
    }
    const found = wilayas.find((w) => w.name === form.wilaya);
    const fee = found
      ? (deliveryType === "office" ? found.office_price : found.home_price)
      : null;
    setDeliveryFee(fee);
    updateWilayaFee(fee);
  }, [form.wilaya, deliveryType, wilayas, updateWilayaFee]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const total = deliveryFee === null
    ? null
    : Math.max(0, subtotal + deliveryFee - discount);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.wilaya) { toast.error("Veuillez sélectionner une wilaya"); return; }
    if (deliveryFee === null) { toast.error("Prix de livraison introuvable pour cette wilaya"); return; }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.productId, shade: i.shade, quantity: i.quantity })),
        customer: {
          full_name: `${form.first_name} ${form.last_name}`.trim(),
          phone: form.phone,
          email: form.email || null,
        },
        delivery: {
          wilaya: form.wilaya,
          commune: form.commune || null,
          address: form.address,
          delivery_notes: form.delivery_notes || null,
          delivery_type: deliveryType,
        },
        coupon_code: coupon?.code || null,
      };
      const { data } = await api.post("/orders", payload);
      clearCart();
      toast.success("Votre commande The Queen a été confirmée");
      navigate(`/order/${data.id}/confirmation`);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Erreur lors de la validation");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-queen pt-32 pb-24" id="main">
      <h1 className="font-display text-[#111111] mb-10" style={{ fontSize: "clamp(2.5rem, 5vw, 3.5rem)" }}>
        Finaliser votre Rituel
      </h1>

      <form onSubmit={submit} className="grid lg:grid-cols-[1fr_400px] gap-12">
        <div className="space-y-10">
          {/* Customer info */}
          <section className="bg-white p-6 sm:p-8 rounded-3xl border border-[#EAE8E4]">
            <h2 className="font-display text-2xl text-[#111111] mb-6">Vos Coordonnées</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className="label-eyebrow text-[#666666] block mb-2">Prénom</label><input required value={form.first_name} onChange={set("first_name")} className={inputCls} data-testid="checkout-firstname" /></div>
              <div><label className="label-eyebrow text-[#666666] block mb-2">Nom</label><input required value={form.last_name} onChange={set("last_name")} className={inputCls} data-testid="checkout-lastname" /></div>
              <div><label className="label-eyebrow text-[#666666] block mb-2">Numéro de téléphone</label><input required value={form.phone} onChange={set("phone")} placeholder="05 / 06 / 07..." className={inputCls} data-testid="checkout-phone" /></div>
              <div><label className="label-eyebrow text-[#666666] block mb-2">Email (pour le reçu)</label><input type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="checkout-email" /></div>
            </div>
          </section>

          {/* Delivery */}
          <section className="bg-white p-6 sm:p-8 rounded-3xl border border-[#EAE8E4]">
            <h2 className="font-display text-2xl text-[#111111] mb-6">Adresse de Livraison</h2>
            <div className="space-y-5">
              {/* Wilaya */}
              <div>
                <label className="label-eyebrow text-[#666666] block mb-2">Wilaya de destination</label>
                <Select value={form.wilaya} onValueChange={(v) => setForm({ ...form, wilaya: v })}>
                  <SelectTrigger className="border border-[#EAE8E4] rounded-xl h-12" data-testid="checkout-wilaya">
                    <SelectValue placeholder="Sélectionnez votre wilaya (58 wilayas)" />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {wilayas.map((w) => (
                      <SelectItem key={w.code} value={w.name}>{w.code} — {w.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Delivery type */}
              {form.wilaya && (
                <div>
                  <label className="label-eyebrow text-[#666666] block mb-3">Mode de Réception</label>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {[
                      {
                        value: "home",
                        icon: HomeIcon,
                        label: "À domicile",
                        desc: form.wilaya && wilayas.find((w) => w.name === form.wilaya)
                          ? formatPrice(wilayas.find((w) => w.name === form.wilaya).home_price)
                          : "—",
                      },
                      {
                        value: "office",
                        icon: Building2,
                        label: "Bureau / Stop Desk",
                        desc: form.wilaya && wilayas.find((w) => w.name === form.wilaya)
                          ? formatPrice(wilayas.find((w) => w.name === form.wilaya).office_price)
                          : "—",
                      },
                    ].map(({ value, icon: Icon, label, desc }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setDeliveryType(value)}
                        className={`flex items-center gap-4 rounded-2xl border-2 p-4 text-left transition-all ${
                          deliveryType === value
                            ? "border-[#111111] bg-[#F8F8F6]"
                            : "border-[#EAE8E4] hover:border-[#CCCCCC]"
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-full grid place-items-center ${deliveryType === value ? "bg-[#111111] text-white" : "bg-[#F8F8F6] text-[#666666]"}`}>
                          <Icon size={18} />
                        </div>
                        <div className="flex-1">
                          <p className="font-display text-[#111111] text-base font-medium">{label}</p>
                          <p className="text-xs text-[#666666] mt-0.5">{desc}</p>
                        </div>
                        {deliveryType === value && (
                          <div className="w-5 h-5 rounded-full bg-[#111111] text-white grid place-items-center shrink-0">
                            <Check size={12} />
                          </div>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div><label className="label-eyebrow text-[#666666] block mb-2">Commune</label><input value={form.commune} onChange={set("commune")} className={inputCls} data-testid="checkout-commune" /></div>
              <div><label className="label-eyebrow text-[#666666] block mb-2">Adresse détaillée</label><textarea required value={form.address} onChange={set("address")} rows={2} className={inputCls} data-testid="checkout-address" /></div>
              <div><label className="label-eyebrow text-[#666666] block mb-2">Instructions pour le coursier (optionnel)</label><textarea value={form.delivery_notes} onChange={set("delivery_notes")} rows={2} className={inputCls} data-testid="checkout-notes" /></div>
            </div>
          </section>

          {/* Payment Method Notice */}
          <section className="bg-[#F8F8F6] p-6 rounded-2xl border border-[#EAE8E4] flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-white grid place-items-center text-[#111111] border border-[#EAE8E4] shrink-0">
              <Banknote size={22} />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#111111]">Paiement à la livraison (Cash on Delivery)</p>
              <p className="text-xs text-[#666666] mt-0.5">Vous réglez le montant en espèces directement au livreur lors de la réception de votre colis.</p>
            </div>
          </section>
        </div>

        {/* Order Summary Sidebar */}
        <div>
          <div className="bg-white p-6 sm:p-8 rounded-3xl border border-[#EAE8E4] sticky top-28 space-y-6">
            <h2 className="font-display text-2xl text-[#111111]">Récapitulatif</h2>

            <div className="space-y-3 max-h-60 overflow-y-auto pr-1">
              {items.map((it) => (
                <div key={it.key} className="flex items-center gap-3 py-2 border-b border-[#F8F8F6]">
                  <img src={mediaUrl(it.image)} alt="" className="w-12 h-14 object-contain bg-[#F8F8F6] rounded-lg p-1 mix-blend-multiply" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-[#111111] line-clamp-1">{it.name}</p>
                    <p className="text-[11px] text-[#888888]">Qté : {it.quantity}</p>
                  </div>
                  <span className="text-xs font-semibold text-[#111111]">{formatPrice(it.price * it.quantity)}</span>
                </div>
              ))}
            </div>

            <CouponInput subtotal={subtotal} />

            <div className="space-y-2.5 pt-4 border-t border-[#EAE8E4] text-xs font-body">
              <div className="flex justify-between text-[#666666]">
                <span>Sous-total</span>
                <span className="text-[#111111] font-medium">{formatPrice(subtotal)}</span>
              </div>
              {discount > 0 && (
                <div className="flex justify-between text-emerald-700">
                  <span>Remise privilège</span>
                  <span>-{formatPrice(discount)}</span>
                </div>
              )}
              <div className="flex justify-between text-[#666666]">
                <span>Livraison ({deliveryType === "office" ? "Stop Desk" : "Domicile"})</span>
                <span className="text-[#111111]">
                  {deliveryFee === null ? "Sélectionnez une wilaya" : formatPrice(deliveryFee)}
                </span>
              </div>
              <div className="flex justify-between items-baseline pt-4 border-t border-[#EAE8E4]">
                <span className="font-display text-lg text-[#111111]">Total net</span>
                <span className="font-body text-2xl font-bold text-[#111111]">
                  {total === null ? `${formatPrice(subtotal - discount)} + livr.` : formatPrice(total)}
                </span>
              </div>
            </div>

            <button
              type="submit"
              disabled={submitting || deliveryFee === null}
              className="btn-queen-primary w-full h-12 text-xs uppercase tracking-wider"
              data-testid="checkout-submit"
            >
              {submitting ? "Validation..." : "Confirmer ma commande"}
            </button>

            <div className="flex items-center justify-center gap-2 text-[11px] text-[#888888] pt-2">
              <ShieldCheck size={14} className="text-[#C5A880]" />
              Garantie d'authenticité The Queen
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}
